/* eslint-disable @typescript-eslint/no-empty-function */
import {
  Component,
  HostBinding,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  ViewChildren,
  QueryList,
  ElementRef,
  AfterViewInit
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'umss-input-otp',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './input-otp.component.html',
  styleUrls: ['./input-otp.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UmssInputOtpComponent),
      multi: true
    }
  ]
})
export class UmssInputOtpComponent
  implements ControlValueAccessor, AfterViewInit
{
  @HostBinding('style.display') display = 'contents';

  @ViewChildren('otpInput') otpInputs!: QueryList<ElementRef<HTMLInputElement>>;

  /** Content props */
  @Input() length: number = 6;
  @Input() placeholder: string = '0';
  @Input() separator?: string; // e.g., "•" or "-"
  @Input() separatorPosition?: number; // Position to insert separator (e.g., 3 for "123•456")

  /** Variant props */
  @Input() size: 'Mini' | 'Small' | 'Regular' | 'Large' = 'Regular';

  /** State props */
  @Input() disabled: boolean = false;
  @Input() error: boolean = false;
  @Input() required: boolean = false;

  /** Output events */
  @Output() valueChange = new EventEmitter<string>();
  @Output() complete = new EventEmitter<string>();

  /** Internal state */
  otpValues: string[] = [];
  private _value: string = '';

  constructor() {
    this.initializeOtpValues();
  }

  ngAfterViewInit() {
    // Focus first input on init if not disabled
    if (!this.disabled && this.otpInputs.first) {
      setTimeout(() => this.otpInputs.first.nativeElement.focus(), 0);
    }
  }

  private initializeOtpValues() {
    this.otpValues = Array(this.length).fill('');
  }

  /** ControlValueAccessor implementation */
  private _onChange: (value: string) => void = () => {};
  private _onTouched: () => void = () => {};

  writeValue(value: string): void {
    if (value) {
      this._value = value;
      const chars = value.split('').slice(0, this.length);
      this.otpValues = [
        ...chars,
        ...Array(this.length - chars.length).fill('')
      ];
    } else {
      this.initializeOtpValues();
      this._value = '';
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /** Event handlers */
  onInput(event: Event, index: number) {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    // Only allow single digit/character
    if (value.length > 1) {
      input.value = value.charAt(value.length - 1);
    }

    this.otpValues[index] = input.value;
    this.updateValue();

    // Auto-focus next input
    if (input.value && index < this.length - 1) {
      const nextInput = this.otpInputs.toArray()[index + 1];
      if (nextInput) {
        nextInput.nativeElement.focus();
      }
    }
  }

  onKeyDown(event: KeyboardEvent, index: number) {
    const input = event.target as HTMLInputElement;

    // Handle backspace
    if (event.key === 'Backspace') {
      if (!input.value && index > 0) {
        // Move to previous input if current is empty
        const prevInput = this.otpInputs.toArray()[index - 1];
        if (prevInput) {
          prevInput.nativeElement.focus();
          prevInput.nativeElement.select();
        }
      }
    }

    // Handle arrow keys
    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      this.otpInputs.toArray()[index - 1].nativeElement.focus();
    }

    if (event.key === 'ArrowRight' && index < this.length - 1) {
      event.preventDefault();
      this.otpInputs.toArray()[index + 1].nativeElement.focus();
    }

    // Handle paste
    if (event.key === 'v' && (event.ctrlKey || event.metaKey)) {
      // Paste will be handled by onPaste event
      return;
    }
  }

  onPaste(event: ClipboardEvent, index: number) {
    event.preventDefault();
    const pastedData = event.clipboardData?.getData('text') || '';
    const chars = pastedData
      .replace(/\s/g, '')
      .split('')
      .slice(0, this.length - index);

    chars.forEach((char, i) => {
      const targetIndex = index + i;
      if (targetIndex < this.length) {
        this.otpValues[targetIndex] = char;
        const input = this.otpInputs.toArray()[targetIndex];
        if (input) {
          input.nativeElement.value = char;
        }
      }
    });

    this.updateValue();

    // Focus the next empty input or last input
    const nextEmptyIndex = this.otpValues.findIndex((v, i) => i > index && !v);
    const focusIndex =
      nextEmptyIndex !== -1
        ? nextEmptyIndex
        : Math.min(index + chars.length, this.length - 1);
    this.otpInputs.toArray()[focusIndex]?.nativeElement.focus();
  }

  onFocus(index: number) {
    const input = this.otpInputs.toArray()[index];
    if (input) {
      input.nativeElement.select();
    }
  }

  onBlur() {
    this._onTouched();
  }

  private updateValue() {
    const newValue = this.otpValues.join('');
    this._value = newValue;
    this._onChange(newValue);
    this.valueChange.emit(newValue);

    // Emit complete event if all digits are filled
    if (newValue.length === this.length && !newValue.includes('')) {
      this.complete.emit(newValue);
    }
  }

  /** Helper methods */
  shouldShowSeparator(index: number): boolean {
    return !!this.separator && this.separatorPosition === index;
  }

  getInputArray(): number[] {
    return Array(this.length)
      .fill(0)
      .map((_, i) => i);
  }
}
